import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";

// Preserve sub-rupiah outstanding display without rounding up.
const formatExactIDR = (value) => "Rp " + Number(value || 0).toLocaleString("id-ID", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const PAY_FILTERS = [["ALL", "Semua"], ["UNPAID", "Belum Bayar"], ["PARTIAL_PAID", "Sebagian"], ["PAID", "Lunas"]];
const PAY_BADGE = {
  UNPAID: { label: "Belum Bayar", dot: "#d92d20", color: "#b42318" },
  PARTIAL_PAID: { label: "Sebagian", dot: "#f79009", color: "#b54708" },
  PAID: { label: "Lunas", dot: "#12b76a", color: "#027a48" },
};

const PRIORITY_META = {
  OVERDUE: { label: "TERLAMBAT", color: "#b42318", bg: "#fff1f0", icon: "🔴" },
  DUE_TODAY: { label: "JATUH TEMPO HARI INI", color: "#b54708", bg: "#fff7e6", icon: "🟠" },
  DUE_SOON: { label: "SEGERA BAYAR", color: "#946200", bg: "#fffbea", icon: "🟡" },
  UPCOMING: { label: "AMAN", color: "#475467", bg: "#f8fafc", icon: "⚪" },
  PAID: { label: "LUNAS", color: "#027a48", bg: "#ecfdf3", icon: "🟢" },
};

function DueBadge({ purchase }) {
  if (purchase.status === "REVERSED") return <span className="status-pill off" data-testid={`due-cancelled-${purchase.purchase_no}`}>DIBATALKAN — tidak jatuh tempo</span>;
  const meta = PRIORITY_META[purchase.payment_priority] || PRIORITY_META.UPCOMING;
  if (purchase.payment_priority === "PAID") return <span className="status-pill on">🟢 LUNAS</span>;
  const days = Number(purchase.days_until_due);
  let text = meta.label;
  if (purchase.payment_priority === "OVERDUE") text = `TERLAMBAT ${Math.abs(days)} hari`;
  else if (purchase.payment_priority === "DUE_TODAY") text = "JATUH TEMPO HARI INI";
  else if (purchase.payment_priority === "DUE_SOON") text = `JATUH TEMPO ${days} hari lagi`;
  else text = `JATUH TEMPO ${days} hari lagi`;
  return <span className="status-pill off" style={{ color: meta.color, borderColor: meta.color, background: meta.bg, fontWeight: 700, whiteSpace: "nowrap" }}>{meta.icon} {text}</span>;
}

function PayBadge({ status, no, purchaseStatus }) {
  if (purchaseStatus === "REVERSED") return <span className="status-pill off" data-testid={`payment-${no}`}>DIBATALKAN</span>;
  const b = PAY_BADGE[status] || PAY_BADGE.UNPAID;
  return (
    <span className="status-pill off" style={{ color: b.color, borderColor: b.color, whiteSpace: "nowrap" }} data-testid={`payment-${no}`}>
      <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: b.dot, marginRight: 6, verticalAlign: "middle" }} />
      {b.label}
    </span>
  );
}

export default function PurchaseList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [supplierNames, setSupplierNames] = useState({});
  const [error, setError] = useState("");
  const [payFilter, setPayFilter] = useState("ALL");
  const [purchaseSearch, setPurchaseSearch] = useState("");
  const normalizedSearch = purchaseSearch.trim().toLocaleLowerCase("id-ID");
  const visible = (payFilter === "ALL" ? purchases : purchases.filter((p) => p.status !== "REVERSED" && (p.payment_status || "UNPAID") === payFilter))
    .filter((p) => !normalizedSearch || [p.purchase_no, p.supplier_name, supplierNames[p.supplier_id]].some((v) => String(v || "").toLocaleLowerCase("id-ID").includes(normalizedSearch)))
    .slice()
    .sort((a, b) => {
      const rank = { OVERDUE: 0, DUE_TODAY: 1, DUE_SOON: 2, UPCOMING: 3, PAID: 4 };
      return (rank[a.payment_priority] ?? 9) - (rank[b.payment_priority] ?? 9) || String(a.due_date).localeCompare(String(b.due_date));
    });
  const reminders = purchases
    .filter((p) => p.status !== "REVERSED" && p.payment_priority && p.payment_priority !== "PAID")
    .sort((a, b) => {
      const rank = { OVERDUE: 0, DUE_TODAY: 1, DUE_SOON: 2, UPCOMING: 3 };
      return (rank[a.payment_priority] ?? 9) - (rank[b.payment_priority] ?? 9) || String(a.due_date).localeCompare(String(b.due_date));
    });

  useEffect(() => {
    Promise.all([api.get("/purchases"), api.get("/suppliers")])
      .then(([purchaseRes, supplierRes]) => {
        setPurchases(purchaseRes.data);
        setSupplierNames(Object.fromEntries(supplierRes.data.map((s) => [s.id, s.name])));
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
  }, []);

  if (user && user.role !== "OWNER") {
    return (
      <div className="app-shell">
        <AppTopBar active="purchase" />
        <main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <AppTopBar active="purchase" />
      <main className="content">
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}
        <section className="panel" data-testid="purchase-list-panel">
          <div className="panel-head">
            <h2 className="panel-title">Daftar Purchase ({purchases.length})</h2>
            <Link to="/purchases/new" className="btn-primary inline" data-testid="new-purchase-button">
              + Buat Purchase
            </Link>
          </div>
          {reminders.length > 0 && (
            <div data-testid="payment-reminder-panel" style={{ margin: "0 0 14px", padding: 12, border: "1px solid #f2c46d", borderRadius: 8, background: "#fffaf0" }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>🔔 Pengingat Pembayaran Supplier</div>
              <div style={{ fontSize: 13, marginBottom: 10, color: "#475467" }}>
                Dahulukan pembayaran yang terlambat atau paling dekat jatuh tempo agar hubungan dengan supplier tetap terjaga.
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {reminders.slice(0, 5).map((p) => (
                  <div key={p.id} data-testid={`payment-reminder-${p.purchase_no}`} style={{ display: "grid", gridTemplateColumns: "1.2fr 1.2fr 1fr auto", gap: 10, alignItems: "center", padding: "7px 9px", borderRadius: 6, background: PRIORITY_META[p.payment_priority]?.bg || "#fff", borderLeft: `4px solid ${PRIORITY_META[p.payment_priority]?.color || "#98a2b3"}` }}>
                    <span><b>{p.supplier_name || "Supplier"}</b> · {p.purchase_no}</span>
                    <span><DueBadge purchase={p} /></span>
                    <span>Jatuh tempo <b>{p.due_date}</b></span>
                    <span className="mono" style={{ fontWeight: 800 }}>{formatExactIDR(p.payment_outstanding)}</span>
                  </div>
                ))}
              </div>
              {reminders.length > 5 && <div className="hint" style={{ marginTop: 7 }}>+ {reminders.length - 5} Purchase lain perlu diperhatikan.</div>}
            </div>
          )}

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "0 0 10px" }} data-testid="payment-filter">
            {PAY_FILTERS.map(([key, label]) => (
              <button key={key} type="button" className={`btn-mini ${payFilter === key ? "on" : ""}`}
                style={payFilter === key ? { fontWeight: 700, borderColor: "#1d2939" } : undefined}
                onClick={() => setPayFilter(key)} data-testid={`payment-filter-${key}`}>
                {label}
              </button>
            ))}
            <input type="search" className="field-input" value={purchaseSearch}
              onChange={(event) => setPurchaseSearch(event.target.value)}
              placeholder="Cari No Purchase / Supplier..." aria-label="Cari Purchase"
              data-testid="purchase-search-input"
              style={{ flex: "1 1 230px", minWidth: 190, maxWidth: 360, marginLeft: "auto" }} />
          </div>
          <div className="table-scroll">
            <table className="data-table" data-testid="purchase-table">
              <thead>
                <tr>
                  <th>Purchase No</th>
                  <th>Supplier</th>
                  <th>Tanggal</th>
                  <th>Status</th>
                  <th>Pembayaran</th>
                  <th>Jatuh Tempo</th>
                  <th>Total</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan={8} className="empty-cell">{purchases.length === 0 ? "Belum ada Purchase." : "Tidak ada Purchase dengan status pembayaran ini."}</td></tr>
                )}
                {visible.map((p) => (
                  <tr key={p.id} data-testid={`purchase-row-${p.purchase_no}`}>
                    <td className="mono">{p.purchase_no}</td>
                    <td data-testid={`supplier-${p.purchase_no}`}>
                      {p.supplier_id ? (supplierNames[p.supplier_id] || "Supplier tidak ditemukan") : "Belum dipetakan"}
                    </td>
                    <td>{p.purchase_date}</td>
                    <td><span className={`status-pill ${p.status === "OPEN" ? "on" : "off"}`} style={p.status === "REVERSED" ? { color: "#b42318", borderColor: "#b42318" } : undefined}>{p.status}</span></td>
                    <td><PayBadge status={p.payment_status} no={p.purchase_no} purchaseStatus={p.status} /></td>
                    <td data-testid={`due-${p.purchase_no}`}>
                      <DueBadge purchase={p} />
                      <div className="hint" style={{ marginTop: 3 }}>{p.due_date} · Termin {p.payment_term_days || 0} hari</div>
                    </td>
                    <td className="mono">{formatIDR(p.purchase_total)}</td>
                    <td>
                      <button className="btn-mini" onClick={() => navigate(`/purchases/${p.id}`)} data-testid={`detail-${p.purchase_no}`}>
                        Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
